// Ported from vlookup-dramatic/components/ui/tabs.tsx without Base UI / cva.
// <Tabs defaultValue="a" onValueChange={fn}><TabsList variant="line"><TabsTrigger value="a">…</TabsTrigger></TabsList><TabsContent value="a">…</TabsContent></Tabs>
import React, { createContext, useContext, useState } from "react";
import { cn } from "../../lib/utils";

const Ctx = createContext({ value: undefined, setValue: () => {} });

export function Tabs({ className, orientation = "horizontal", defaultValue, value, onValueChange, children, ...props }) {
  const [internal, setInternal] = useState(defaultValue);
  const current = value !== undefined ? value : internal;
  const setValue = (v) => {
    if (value === undefined) setInternal(v);
    if (onValueChange) onValueChange(v);
  };
  return (
    <Ctx.Provider value={{ value: current, setValue }}>
      <div
        data-slot="tabs"
        data-orientation={orientation}
        className={cn("group/tabs flex gap-2 data-horizontal:flex-col", className)}
        {...props}
      >
        {children}
      </div>
    </Ctx.Provider>
  );
}

const LIST_BASE =
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-horizontal/tabs:h-8 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none";
const LIST_VARIANTS = { default: "bg-muted", line: "gap-1 bg-transparent" };

export function tabsListVariants({ variant = "default" } = {}) {
  return cn(LIST_BASE, LIST_VARIANTS[variant] || LIST_VARIANTS.default);
}

export function TabsList({ className, variant = "default", ...props }) {
  return (
    <div role="tablist" data-slot="tabs-list" data-variant={variant} className={cn(tabsListVariants({ variant }), className)} {...props} />
  );
}

export function TabsTrigger({ className, value, children, ...props }) {
  const ctx = useContext(Ctx);
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-slot="tabs-trigger"
      data-state={active ? "active" : "inactive"}
      onClick={() => ctx.setValue(value)}
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 aria-disabled:pointer-events-none aria-disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent",
        "data-active:bg-background data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground",
        "after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-5px] group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function TabsContent({ className, value, children, ...props }) {
  const ctx = useContext(Ctx);
  if (ctx.value !== value) return null;
  return (
    <div role="tabpanel" data-slot="tabs-content" className={cn("flex-1 text-sm outline-none", className)} {...props}>
      {children}
    </div>
  );
}
